import axios from 'axios';
import { useState, type FormEvent } from 'react';
import { toErrorMessage } from '../api/client';
import {
  fetchIntegrationSettings,
  regenerateWebhookSecret,
  saveIntegrationSettings,
} from '../api/endpoints';
import type {
  AgeVerificationStep,
  ApiErrorBody,
  IntegrationSettings,
  IntegrationSettingsInput,
} from '../api/types';
import { useApiData } from '../hooks/useApiData';
import { ConfirmDialog } from './ConfirmDialog';
import { ErrorState, LoadingState } from './LoadingState';
import { useToast } from './ToastProvider';

/** Mirrors IdVerify.Core.Helpers — the server enforces these; here they only size the inputs. */
const MAX_URL_LENGTH = 2048;
const MAX_ALLOWED_ORIGINS = 20;
const MAX_MINIMUM_AGE = 150;
/** Mirrors IdVerify.Core.Helpers.AgeVerificationFlow (canonical order). */
const AGE_STEP_OPTIONS: { step: AgeVerificationStep; label: string; hint: string }[] = [
  { step: 'liveness', label: 'Liveness check', hint: 'selfie on camera' },
  { step: 'id-front', label: 'Front of ID', hint: 'date of birth read from the photo' },
  { step: 'id-back', label: 'Back of ID', hint: 'date of birth from the barcode' },
  { step: 'face-match', label: 'Face match', hint: 'the selfie must match the photo on the ID; automatic with liveness + front' },
];
/**
 * Face match compares the liveness selfie with the ID front photo, and is always on when both are
 * (AgeVerificationFlow.WithRequiredFaceMatch): a selfie and an ID photo are never left uncompared.
 */
const FACE_MATCH_NEEDS: AgeVerificationStep[] = ['liveness', 'id-front'];
const SIGNATURE_HEADER = 'X-InertiaX-Signature';

type FieldErrors = Partial<Record<keyof IntegrationSettingsInput, string[]>>;

/**
 * Which cards to show. `delivery` = return URL + decision webhook (Developers → Webhooks & redirect);
 * `age` = age verification (Settings → Verification rules). One endpoint saves all of them, so the
 * cards that are not shown are saved with the values they were loaded with.
 */
export type IntegrationSection = 'delivery' | 'age';
const ALL_SECTIONS: IntegrationSection[] = ['delivery', 'age'];

/**
 * Integration settings: customer return URL, age verification and decision webhook. Rendered on
 * Webhooks & redirect and Verification rules (own client — no clientId; read-only unless
 * ClientAdmin) and on Client detail (SuperAdmin, with clientId, every section).
 * React counterpart of web/Pages/Shared/_IntegrationSettings.cshtml; rules in Core
 * (ReturnUrls, WebhookUrls, IntegrationSettingsService).
 */
export function IntegrationSettingsForm({
  clientId,
  sections = ALL_SECTIONS,
  readOnly = false,
}: {
  clientId?: number;
  sections?: IntegrationSection[];
  readOnly?: boolean;
}) {
  const { data, loading, error } = useApiData(() => fetchIntegrationSettings(clientId), [clientId]);

  if (loading) return <LoadingState label="Loading integration settings" />;
  if (error) return <ErrorState message={error} />;
  if (!data) return <ErrorState message="Integration settings could not be loaded." />;

  return <Form clientId={clientId} initial={data} sections={sections} readOnly={readOnly} />;
}

function Form({
  clientId,
  initial,
  sections,
  readOnly,
}: {
  clientId?: number;
  initial: IntegrationSettings;
  sections: IntegrationSection[];
  readOnly: boolean;
}) {
  const showDelivery = sections.includes('delivery');
  const showAge = sections.includes('age');
  const { showSuccess, showError } = useToast();
  const [settings, setSettings] = useState(initial);
  const [defaultUrl, setDefaultUrl] = useState(initial.defaultRedirectUrl ?? '');
  const [originsText, setOriginsText] = useState(initial.allowedRedirectOrigins.join('\n'));
  const [webhookUrl, setWebhookUrl] = useState(initial.webhookUrl ?? '');
  const [minimumAge, setMinimumAge] = useState(initial.minimumAge?.toString() ?? '');
  const [ageSteps, setAgeSteps] = useState<AgeVerificationStep[]>(initial.ageVerificationSteps);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  function applySaved(saved: IntegrationSettings) {
    // Show the normalised values the server stored.
    setSettings(saved);
    setDefaultUrl(saved.defaultRedirectUrl ?? '');
    setOriginsText(saved.allowedRedirectOrigins.join('\n'));
    setWebhookUrl(saved.webhookUrl ?? '');
    setMinimumAge(saved.minimumAge?.toString() ?? '');
    setAgeSteps(saved.ageVerificationSteps);
    setNewSecret(saved.newWebhookSecret);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setFieldErrors({});
    try {
      applySaved(
        await saveIntegrationSettings(
          {
            defaultRedirectUrl: defaultUrl.trim() || null,
            allowedRedirectOrigins: originsText.split('\n'),
            webhookUrl: webhookUrl.trim() || null,
            minimumAge: minimumAge.trim() === '' ? null : Number(minimumAge),
            ageVerificationSteps: ageSteps,
          },
          clientId,
        ),
      );
      showSuccess(showDelivery && showAge ? 'Integration settings saved.' : 'Settings saved.');
    } catch (err) {
      const errors = axios.isAxiosError(err)
        ? (err.response?.data as ApiErrorBody | undefined)?.errors
        : undefined;
      if (errors) setFieldErrors(errors as FieldErrors);
      showError(toErrorMessage(err, 'An error occurred while saving. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleRegenerate() {
    setRegenerating(true);
    try {
      applySaved(await regenerateWebhookSecret(clientId));
      showSuccess('New webhook signing secret created.');
    } catch (err) {
      showError(toErrorMessage(err));
    } finally {
      setRegenerating(false);
      setConfirmRegenerate(false);
    }
  }

  const defaultErrors = fieldErrors.defaultRedirectUrl ?? [];
  const originErrors = fieldErrors.allowedRedirectOrigins ?? [];
  const webhookErrors = fieldErrors.webhookUrl ?? [];
  const minAgeErrors = fieldErrors.minimumAge ?? [];
  const stepsErrors = fieldErrors.ageVerificationSteps ?? [];

  function toggleAgeStep(step: AgeVerificationStep, checked: boolean) {
    setAgeSteps((current) => {
      const next = AGE_STEP_OPTIONS.map((o) => o.step).filter((s) =>
        s === step ? checked : current.includes(s),
      );
      // Face match follows liveness + ID front: on with both, off without either.
      const withoutMatch = next.filter((s) => s !== 'face-match');
      return FACE_MATCH_NEEDS.every((s) => next.includes(s)) ? [...withoutMatch, 'face-match'] : withoutMatch;
    });
  }

  return (
    <>
      {showDelivery && newSecret && (
        <div className="alert alert-warning" role="alert">
          <div className="fw-semibold mb-1">
            <i className="bi bi-key-fill me-2" />
            New webhook signing secret — copy it now
          </div>
          <p className="small mb-2">
            It won't be shown again. Use it to check the <code>{SIGNATURE_HEADER}</code> header on
            each delivery.
          </p>
          <div className="input-group">
            <input type="text" className="form-control font-monospace" readOnly value={newSecret} />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => void navigator.clipboard.writeText(newSecret)}
            >
              <i className="bi bi-clipboard" /> Copy
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* A disabled fieldset turns every input inside read-only in one place. */}
        <fieldset disabled={readOnly} className="m-0 p-0 border-0" style={{ minWidth: 0 }}>
          {showDelivery && (
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0 fw-semibold">
                  <i className="bi bi-box-arrow-up-right me-2 text-primary" />
                  Customer Return URL
                </h6>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <label htmlFor="defaultRedirectUrl" className="form-label fw-semibold">
                    Default return URL
                  </label>
                  <input
                    id="defaultRedirectUrl"
                    type="url"
                    className={`form-control ${defaultErrors.length ? 'is-invalid' : ''}`}
                    placeholder="https://shop.example/checkout/verified"
                    maxLength={MAX_URL_LENGTH}
                    value={defaultUrl}
                    onChange={(e) => setDefaultUrl(e.target.value)}
                  />
                  {defaultErrors.map((m) => (
                    <div className="invalid-feedback" key={m}>
                      {m}
                    </div>
                  ))}
                  <div className="form-text">
                    Used when a session is created without its own <code>redirectUrl</code>. We add{' '}
                    <code>verificationId</code> and <code>decision</code> to the query string. Approved
                    customers are sent there automatically; everyone else gets a Return button. The
                    decision in the URL is only a hint — confirm it with{' '}
                    <code>GET /v1/verifications/{'{id}'}</code> or the webhook below. Leave blank to
                    keep customers on the result screen. Live API keys need https.
                  </div>
                </div>

                <div>
                  <label htmlFor="allowedRedirectOrigins" className="form-label fw-semibold">
                    Allowed return origins
                  </label>
                  <textarea
                    id="allowedRedirectOrigins"
                    rows={4}
                    className={`form-control font-monospace ${originErrors.length ? 'is-invalid' : ''}`}
                    placeholder={'https://shop.example\nhttps://*.shop.example'}
                    value={originsText}
                    onChange={(e) => setOriginsText(e.target.value)}
                  />
                  {originErrors.map((m) => (
                    <div className="invalid-feedback" key={m}>
                      {m}
                    </div>
                  ))}
                  <div className="form-text">
                    One origin per line (scheme and host, no path), up to {MAX_ALLOWED_ORIGINS}. Each
                    entry is exact: <code>https://shop.example</code> allows only that host. To allow
                    subdomains, add a wildcard entry: <code>https://*.shop.example</code> allows{' '}
                    <code>a.shop.example</code> and <code>a.b.shop.example</code>, but not{' '}
                    <code>shop.example</code> itself. A session's <code>redirectUrl</code> must match
                    one of these or the default URL's origin, or it is refused with{' '}
                    <code>REDIRECT_URL_NOT_ALLOWED</code>, so a leaked API key can't send customers
                    anywhere else. The same list decides which of your sites may embed the verification
                    page with <code>verify.js</code>. With no default and no origins, any URL is accepted
                    and any site may embed it.
                  </div>
                </div>
              </div>
            </div>
          )}

          {showAge && (
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0 fw-semibold">
                  <i className="bi bi-person-check me-2 text-warning" />
                  Age Verification
                </h6>
              </div>
              <div className="card-body">
                <label htmlFor="minimumAge" className="form-label fw-semibold">
                  Minimum age
                </label>
                <input
                  id="minimumAge"
                  type="number"
                  min={1}
                  max={MAX_MINIMUM_AGE}
                  step={1}
                  style={{ maxWidth: '10rem' }}
                  className={`form-control ${minAgeErrors.length ? 'is-invalid' : ''}`}
                  placeholder="None"
                  value={minimumAge}
                  onChange={(e) => setMinimumAge(e.target.value)}
                />
                {minAgeErrors.map((m) => (
                  <div className="invalid-feedback" key={m}>
                    {m}
                  </div>
                ))}
                <div className="form-text">
                  The lowest age an age-verification session can check for. A session's{' '}
                  <code>ageLimit</code> below this is raised to it, so a customer under this age is never
                  approved. A higher <code>ageLimit</code> is kept. Also used when a session sends no{' '}
                  <code>ageLimit</code>. Leave blank for no floor; each session must then send its own{' '}
                  <code>ageLimit</code>.
                </div>

                <div className="mt-4">
                  <div className="form-label fw-semibold">Verification steps</div>
                  {AGE_STEP_OPTIONS.map((o) => (
                    <div className="form-check" key={o.step}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`ageStep-${o.step}`}
                        checked={ageSteps.includes(o.step)}
                        disabled={o.step === 'face-match'}
                        onChange={(e) => toggleAgeStep(o.step, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={`ageStep-${o.step}`}>
                        {o.label} <span className="text-body-secondary">({o.hint})</span>
                      </label>
                    </div>
                  ))}
                  {stepsErrors.map((m) => (
                    <div className="text-danger small mt-1" key={m}>
                      {m}
                    </div>
                  ))}
                  <div className="form-text">
                    What a customer is asked to do in an age-verification session that doesn't send its
                    own <code>steps</code>. Choose at least one side of the ID, because the date of
                    birth can only be read from the document. Face match compares the selfie with the
                    photo on the front of the ID; it is turned on automatically whenever the liveness
                    check and the front of the ID are both chosen, and is off otherwise. Fewer steps are
                    faster for the customer;
                    every step together is the strictest. A session may still override this by sending{' '}
                    <code>steps</code>.
                  </div>
                </div>
              </div>
            </div>
          )}

          {showDelivery && (
            <div className="card mb-4">
              <div className="card-header">
                <h6 className="mb-0 fw-semibold">
                  <i className="bi bi-broadcast me-2 text-success" />
                  Decision Webhook
                </h6>
              </div>
              <div className="card-body">
                <label htmlFor="webhookUrl" className="form-label fw-semibold">
                  Webhook URL
                </label>
                <input
                  id="webhookUrl"
                  type="url"
                  className={`form-control ${webhookErrors.length ? 'is-invalid' : ''}`}
                  placeholder="https://shop.example/webhooks/idverify"
                  maxLength={MAX_URL_LENGTH}
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                />
                {webhookErrors.map((m) => (
                  <div className="invalid-feedback" key={m}>
                    {m}
                  </div>
                ))}
                <div className="form-text">
                  When a customer finishes, we POST{' '}
                  <code>{'{ verificationId, status, decision, reasons, completedAt }'}</code> here,
                  signed with HMAC-SHA256 in the <code>{SIGNATURE_HEADER}</code> header (
                  <code>t=…,v1=…</code>). Must be a public https address
                  {settings.allowPrivateWebhookTargets &&
                    ' (http and localhost are allowed in this development environment)'}
                  . Redirects are not followed. Saving a URL for the first time creates a signing
                  secret; clearing it turns the webhook off and deletes the secret.
                </div>

                <div className="d-flex align-items-center gap-2 mt-3 small">
                  <span className="fw-semibold">Signing secret:</span>
                  {settings.webhookSecretSet ? (
                    <span className="font-monospace">whsec_••••{settings.webhookSecretHint}</span>
                  ) : (
                    <span className="text-body-secondary">none (set a URL to create one)</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </fieldset>

        {readOnly ? (
          <p className="small text-body-secondary mb-0">
            <i className="bi bi-lock me-1" />
            Only a company admin can change these settings.
          </p>
        ) : (
          <div className="d-flex flex-wrap gap-2">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <i className="bi bi-save me-1" />
              {saving ? 'Saving…' : 'Save settings'}
            </button>
            {showDelivery && settings.webhookSecretSet && (
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={() => setConfirmRegenerate(true)}
              >
                <i className="bi bi-arrow-repeat me-1" />
                Regenerate signing secret
              </button>
            )}
          </div>
        )}
      </form>

      <ConfirmDialog
        show={confirmRegenerate}
        title="Regenerate signing secret?"
        message="The current secret stops working immediately. Deliveries are signed with the new one from now on."
        confirmLabel="Regenerate"
        busy={regenerating}
        onConfirm={() => void handleRegenerate()}
        onCancel={() => setConfirmRegenerate(false)}
      />
    </>
  );
}
